import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, Pipe, NgModule } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import * as i2 from '@usana/ux/ssr';
import * as i3 from '@angular/common/http';
import { CommonModule } from '@angular/common';

class TranslationConfig {
    constructor() {
        this.processFreq = 300;
        this.subject = new ReplaySubject();
    }
    /**
     * this adds a package that we can translate by
     */
    addPackage(pkgConfig) {
        this.subject.next(pkgConfig);
    }
    getPackageSubject() {
        return this.subject;
    }
    setProcessFreq(freq) {
        this.processFreq = freq;
    }
    getProcessFreq() {
        return this.processFreq;
    }
    setMarketId(marketId) {
        this.marketId = marketId;
    }
    getMarketId() {
        return this.marketId;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationConfig, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationConfig, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationConfig, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

var PackagePrimeMode;
(function (PackagePrimeMode) {
    PackagePrimeMode[PackagePrimeMode["NO_PRIMING"] = 0] = "NO_PRIMING";
    PackagePrimeMode[PackagePrimeMode["ON_INIT"] = 1] = "ON_INIT";
    PackagePrimeMode[PackagePrimeMode["ON_FIRST_REQUEST"] = 2] = "ON_FIRST_REQUEST";
})(PackagePrimeMode || (PackagePrimeMode = {}));

const graphQueries = {
    getTranslations: {
        query: `
        query getTranslations($pkg: String!, $localeId: String!, $tags: [String]) {
            getTranslations(pkg: $pkg, localeId: $localeId, tags: $tags) {
                marketId,
                translations {
                    localeId,
                    tag,
                    value
                }
            }
        }`,
        operationName: 'getTranslations',
    },
    getDirtyTranslationsInBundleSince: {
        query: `
        query getDirtyTranslationsInBundleSince($pkg: String!, $localeId: String!, $timestamp: Long!) {
            getDirtyTranslationsInBundleSince(pkg: $pkg, localeId: $localeId, timestamp: $timestamp) {
                marketId,
                timestamp,
                translations {
                    localeId,
                    tag,
                    value
                }
            }
        }`,
        operationName: 'getDirtyTranslationsInBundleSince',
    },
    getI18NInfo: {
        query: `
        query getI18NInfo($marketQuery: MarketQuery!) {
            getI18NInfo(marketQuery: $marketQuery) {
                translationMarketId
            }
        }`,
        operationName: 'getI18NInfo',
    },
};
const graphMutations = {
    createTranslationDeepLinkSet: {
        mutation: `
      mutation createTranslationDeepLinkSet($set: DeepLinkTranslationSetInput!) {
        createTranslationDeepLinkSet(set: $set) {
          id,
          pkg
        }
      }`,
        operationName: 'createTranslationDeepLinkSet',
    },
};

class TranslationPromiseWrapper {
}

class TranslationPackageRequests {
    constructor(config) {
        this.needsPriming = config.primeMode === PackagePrimeMode.ON_INIT;
        this.processing = false;
        this.showTagWhenMissing = config.showTagWhenMissing;
        this.promises = {};
    }
    request(tag) {
        if (!this.promises[tag]) {
            const p = new TranslationPromiseWrapper();
            // this is how we keep the resolve and reject for later
            p.promise = new Promise((resolve, reject) => {
                p.resolve = resolve;
                p.reject = reject;
            });
            this.promises[tag] = p;
        }
        return this.promises[tag].promise;
    }
}

class TranslationKey {
    constructor(locale, timestamp) {
        this.locale = locale;
        this.timestamp = timestamp;
    }
}

const STORAGE_PREFIX = 'translation-';
const STORAGE_T_KEY_POSTFIX = 'tKey';

class TranslationPackageState {
    constructor(config, platformWrappedStorageService) {
        this.config = config;
        this.storageService = platformWrappedStorageService;
        this.empty();
    }
    loadTKey(locale) {
        if (this.storageService.hasStorage()) {
            const strg = this.storageService.localStorage().getItem(this.pkgStorageTKey());
            if (!!strg) {
                const keyInfo = JSON.parse(strg);
                if (keyInfo.locale === locale) {
                    this.timestamp = keyInfo.timestamp;
                    return this.timestamp;
                }
            }
            // if we didn't return we either didn't have a key or it was of the wrong locale
            this.dumpStorage(); // lets clear out what we have for this package
            this.timestamp = 0;
            if (this.config.primeMode !== PackagePrimeMode.NO_PRIMING) {
                // if we want to prime be not 0
                this.timestamp = 1;
            }
        }
        return this.timestamp;
    }
    loadStorage() {
        if (this.storageService.hasStorage()) {
            const strg = this.storageService.localStorage().getItem(this.pkgStorageKey());
            if (!!strg) {
                this.current = JSON.parse(strg);
            }
        }
        if (!this.current) {
            // just in case
            this.current = {};
        }
    }
    saveStorage(locale) {
        if (this.storageService.hasStorage()) {
            // put current values in storage but we rip out the missing values as we don't want to store those
            this.storageService.localStorage().setItem(this.pkgStorageKey(), JSON.stringify(this.current, (key, value) => {
                if (this.missing[key]) {
                    return undefined;
                }
                return value;
            }));
            this.storageService.localStorage().setItem(this.pkgStorageTKey(), JSON.stringify(new TranslationKey(locale, this.timestamp)));
        }
    }
    makeValuesTags() {
        for (const tag of Object.keys(this.current)) {
            this.current[tag] = this.config.pkg + ':' + tag;
        }
    }
    missingTranslation(tag) {
        this.missing[tag] = tag;
        if (this.config.showTagWhenMissing) {
            this.current[tag] = this.config.pkg + ':' + tag;
        }
        else {
            this.current[tag] = '';
        }
    }
    validTranslation(tag, value) {
        this.current[tag] = value;
    }
    dump() {
        this.dumpStorage();
        this.empty();
        // we hard set our timestamp to 1 so that we can reload the full package.
        this.timestamp = 1;
    }
    empty() {
        this.current = {};
        this.missing = {};
        this.requests = new TranslationPackageRequests(this.config);
        this.timestamp = 0;
    }
    pkgStorageKey() {
        return STORAGE_PREFIX + this.config.pkg;
    }
    pkgStorageTKey() {
        return this.pkgStorageKey() + '-' + STORAGE_T_KEY_POSTFIX;
    }
    dumpStorage() {
        if (this.storageService.hasStorage()) {
            this.storageService.localStorage().removeItem(this.pkgStorageKey());
            this.storageService.localStorage().removeItem(this.pkgStorageTKey());
        }
    }
}

class TranslationPackage {
    constructor(config, storageService) {
        this.config = config;
        this.state = new TranslationPackageState(config, storageService);
    }
    needsPriming() {
        return this.state.requests.needsPriming;
    }
    setNeedsPriming(priming) {
        this.state.requests.needsPriming = priming;
    }
    loadTKey(locale) {
        return this.state.loadTKey(locale);
    }
    loadStorage() {
        this.state.loadStorage();
    }
    saveStorage(locale) {
        this.state.saveStorage(locale);
    }
    makeValuesTags() {
        this.state.makeValuesTags();
    }
    missingTranslation(tag) {
        this.state.missingTranslation(tag);
    }
    validTranslation(tag, value) {
        this.state.validTranslation(tag, value);
    }
    setProcessing(processing) {
        this.state.requests.processing = processing;
    }
    isProcessing() {
        return this.state.requests.processing;
    }
    getTimestamp() {
        return this.state.timestamp;
    }
    setTimestamp(t) {
        this.state.timestamp = t;
    }
    hasTag(tag) {
        return this.state.current[tag] !== undefined;
    }
    getTagValue(tag) {
        return this.state.current[tag];
    }
    getConfig() {
        return this.config;
    }
    request(tag) {
        return this.state.requests.request(tag);
    }
    resolveRequests() {
        for (const r in this.state.requests.promises) {
            if (this.hasTag(r)) {
                this.state.requests.promises[r].resolve(this.getTagValue(r));
                delete this.state.requests.promises[r]; // clear it because we are done with it
            }
        }
        this.state.requests.processing = false;
    }
    requests() {
        return Object.keys(this.state.requests.promises);
    }
    dump() {
        this.state.dump();
    }
}

const GRAPH_URL_TRANSLATION = new InjectionToken('graphql.translation');
const REST_URL_TRANSLATION = new InjectionToken('rest.translation');
const TRANS_TOOL_ADMIN_URI = new InjectionToken('trans-tool-admin.uri');

class InternalTranslationService {
    constructor(translationConfig, storageService, httpClient, graphUri) {
        this.translationConfig = translationConfig;
        this.storageService = storageService;
        this.httpClient = httpClient;
        this.graphUri = graphUri;
        this.pkgs = {};
        this.rejectedPkgs = new Set();
        this.pkgConfigSubject = translationConfig.getPackageSubject();
        this.pkgConfigSubject.subscribe((pkgConf) => {
            if (!this.pkgs[pkgConf.pkg]) {
                this.initPackage(pkgConf);
            }
        });
    }
    isDebugMode() {
        return this.debugMode;
    }
    toggleDebugMode() {
        this.debugMode = !this.debugMode;
        if (this.isDebugMode()) {
            for (const pkg of Object.keys(this.pkgs)) {
                this.pkgs[pkg].makeValuesTags();
            }
        }
        else {
            // reload from local storage
            for (const pkg of Object.keys(this.pkgs)) {
                this.pkgs[pkg].loadStorage();
            }
        }
    }
    setMarketId(marketId) {
        const prev = this.translationConfig.getMarketId();
        if (!marketId) {
            marketId = null;
        }
        if (prev !== marketId) {
            this.translationConfig.setMarketId(marketId);
            for (const pkg of Object.keys(this.pkgs)) {
                this.initPackage(this.pkgs[pkg].getConfig());
            }
        }
    }
    translate(pkg, tag) {
        if (this.hasTag(pkg, tag)) {
            return this.getTagValue(pkg, tag);
        }
        else {
            this.request(pkg, tag);
            return null;
        }
    }
    translateAsPromise(pkg, tag) {
        if (this.hasTag(pkg, tag)) {
            return Promise.resolve(this.getTagValue(pkg, tag));
        }
        else {
            return this.request(pkg, tag);
        }
    }
    translateMultiAsPromise(pkg, tags) {
        const promiseArr = [];
        for (const tag of tags) {
            promiseArr.push(this.translateAsPromise(pkg, tag));
        }
        return new Promise((resolve, reject) => {
            Promise.all(promiseArr).then((translations) => {
                const result = {};
                for (let j = 0; j < tags.length; j++) {
                    result[tags[j]] = translations[j];
                }
                resolve(result);
            });
        });
    }
    getDirtyTranslationsForPackage(pkgName) {
        const pkg = this.pkgs[pkgName];
        pkg.setProcessing(true);
        return this.httpClient
            .post(this.graphUri, {
            query: graphQueries.getDirtyTranslationsInBundleSince.query,
            variables: {
                pkg: pkgName,
                localeId: this.translationConfig.getMarketId(),
                timestamp: pkg.getTimestamp(),
            },
        })
            .toPromise()
            .then((result) => {
            const bundle = result.data[graphQueries.getDirtyTranslationsInBundleSince.operationName];
            if (bundle) {
                pkg.setTimestamp(bundle.timestamp);
                this.processTranslationResults(pkgName, bundle.marketId, bundle.translations);
            }
            else {
                this.resolveRequests(pkgName); // no data so lets keep moving
            }
        }, (err) => {
            // lets keep trucking when it fails
            this.resolveRequests(pkgName);
        });
    }
    dumpDataForPackage(pkgName) {
        this.pkgs[pkgName].dump();
    }
    hasMarket() {
        // todo this should be different
        return !!this.translationConfig.getMarketId();
    }
    hasPackage(pkg) {
        return !!this.pkgs[pkg];
    }
    hasTag(pkg, tag) {
        return this.hasPackage(pkg) && this.pkgs[pkg].hasTag(tag);
    }
    getTagValue(pkg, tag) {
        return this.pkgs[pkg].getTagValue(tag);
    }
    request(pkg, tag) {
        if (this.hasPackage(pkg)) {
            const ret = this.pkgs[pkg].request(tag);
            this.startProcessing();
            return ret;
        }
        else if (!this.rejectedPkgs.has(pkg)) {
            this.rejectedPkgs.add(pkg);
            return Promise.reject('invalid package'); // so we only reject once
        }
        else {
            return null;
        }
    }
    resolveRequests(pkg) {
        this.pkgs[pkg].resolveRequests(); // this sets processing to false
        this.startProcessing(); // see if we need to start again
    }
    anyRequestsAreProcessing() {
        for (const pkg of Object.keys(this.pkgs)) {
            if (this.pkgs[pkg].isProcessing()) {
                return true;
            }
        }
        return false;
    }
    requestCount() {
        let total = 0;
        for (const pkg of Object.keys(this.pkgs)) {
            if (!this.pkgs[pkg].isProcessing()) {
                total += this.pkgs[pkg].requests().length;
            }
        }
        return total;
    }
    buildDummyTranslationResponse(tags) {
        const allMissing = [];
        for (const tag of tags) {
            // value doesn't matter in this case
            allMissing.push({
                localeId: null,
                tag: tag,
                value: '',
            });
        }
        return allMissing;
    }
    requestTagsInPkg(pkg) {
        if (this.hasMarket() && this.pkgs[pkg].requests().length > 0 && !this.pkgs[pkg].isProcessing()) {
            if (this.pkgs[pkg].needsPriming()) {
                this.pkgs[pkg].setNeedsPriming(false);
                this.primePackage(pkg);
            }
            else {
                const tags = this.pkgs[pkg].requests();
                this.pkgs[pkg].setProcessing(true);
                this.httpClient
                    .post(this.graphUri, {
                    query: graphQueries.getTranslations.query,
                    variables: {
                        pkg: pkg,
                        localeId: this.translationConfig.getMarketId(),
                        tags: tags,
                    },
                })
                    .toPromise()
                    .then((result) => {
                    const translationResult = result.data[graphQueries.getTranslations.operationName];
                    if (translationResult && translationResult.translations) {
                        this.processTranslationResults(pkg, translationResult.marketId, translationResult.translations);
                    }
                    else {
                        this.processTranslationResults(pkg, translationResult.marketId, this.buildDummyTranslationResponse(tags));
                    }
                }, (err) => {
                    // lets keep trucking, make a mock response of everything missing
                    this.processTranslationResults(pkg, this.translationConfig.getMarketId(), this.buildDummyTranslationResponse(tags));
                });
            }
        }
        else {
            // see if we need to start processing again
            this.startProcessing();
        }
    }
    requestTags() {
        for (const pkg of Object.keys(this.pkgs)) {
            this.requestTagsInPkg(pkg);
        }
        this.timer = false;
    }
    processTranslationResults(pkgName, retLocale, translations) {
        const pkg = this.pkgs[pkgName];
        if (retLocale === this.translationConfig.getMarketId() || pkg.getConfig().allowLooseMarketDefinition) {
            // if our locale returned doesn't match the locale we are translating for
            // this means we had a request return after we switched market which is going to cause problems
            // discard those results
            for (const translation of translations) {
                const tag = translation.tag;
                if (!translation.localeId) {
                    pkg.missingTranslation(tag);
                }
                else {
                    pkg.validTranslation(tag, translation.value);
                }
            }
            if (!this.isDebugMode()) {
                pkg.saveStorage(this.translationConfig.getMarketId());
            }
        }
        this.resolveRequests(pkgName);
    }
    startProcessing() {
        if (this.requestCount() > 0 && !this.anyRequestsAreProcessing() && !this.timer) {
            this.timer = true;
            setTimeout(() => {
                this.requestTags();
            }, this.translationConfig.getProcessFreq());
        }
    }
    primePackage(pkgName) {
        const pkg = this.pkgs[pkgName];
        pkg.setProcessing(true);
        // immediately load and resolve what we have, we will backfill as the rest come in
        pkg.loadStorage();
        this.getDirtyTranslationsForPackage(pkgName); // don't care about promise
    }
    initPackage(pkgConfig) {
        this.pkgs[pkgConfig.pkg] = new TranslationPackage(pkgConfig, this.storageService);
        if (this.hasMarket()) {
            const timestamp = this.pkgs[pkgConfig.pkg].loadTKey(this.translationConfig.getMarketId());
            if (pkgConfig.primeMode === PackagePrimeMode.ON_INIT) {
                this.primePackage(pkgConfig.pkg);
            }
            else if (pkgConfig.primeMode === PackagePrimeMode.ON_FIRST_REQUEST || timestamp !== 0) {
                // if we have cached translations from before we have to reprime to make sure nothing has changed
                this.pkgs[pkgConfig.pkg].setNeedsPriming(true);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InternalTranslationService, deps: [{ token: TranslationConfig }, { token: i2.PlatformWrappedStorageService }, { token: i3.HttpClient }, { token: GRAPH_URL_TRANSLATION }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InternalTranslationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InternalTranslationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: TranslationConfig }, { type: i2.PlatformWrappedStorageService }, { type: i3.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [GRAPH_URL_TRANSLATION]
                }] }]; } });

/**
 * publicly available translation methods
 */
class TranslationService {
    constructor(internalTranslationService) {
        this.internalTranslationService = internalTranslationService;
    }
    setMarketId(marketId) {
        this.internalTranslationService.setMarketId(marketId);
    }
    translate(pkg, tag) {
        return this.internalTranslationService.translate(pkg, tag);
    }
    translateAsPromise(pkg, tag) {
        return this.internalTranslationService.translateAsPromise(pkg, tag);
    }
    translateMultiAsPromise(pkg, tags) {
        return this.internalTranslationService.translateMultiAsPromise(pkg, tags);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationService, deps: [{ token: InternalTranslationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: InternalTranslationService }]; } });

class TranslationEditingService {
    constructor(httpClient, graphUri, translationToolAdminUri, internalTranslationService) {
        this.httpClient = httpClient;
        this.graphUri = graphUri;
        this.translationToolAdminUri = translationToolAdminUri;
        this.internalTranslationService = internalTranslationService;
    }
    reloadDirty(pkgName, dumpStorage) {
        if (dumpStorage) {
            this.internalTranslationService.dumpDataForPackage(pkgName);
        }
        return this.internalTranslationService.getDirtyTranslationsForPackage(pkgName);
    }
    isDebugMode() {
        return this.internalTranslationService.isDebugMode();
    }
    toggleDebugMode() {
        this.internalTranslationService.toggleDebugMode();
    }
    getDeepLinkUrl(pkg, marketId, tags) {
        return new Promise((resolve, reject) => {
            this.httpClient
                .post(this.graphUri, {
                query: graphMutations.createTranslationDeepLinkSet.mutation,
                variables: {
                    set: {
                        pkg: pkg,
                        marketId: marketId,
                        tags: tags,
                    },
                },
            })
                .toPromise()
                .then((result) => {
                if (!!result.data.errors && result.data.errors.length > 0) {
                    reject(result.data.errors[0].message);
                }
                else {
                    const deepLinkId = result.data[graphMutations.createTranslationDeepLinkSet.operationName].id;
                    const deepLinkPkg = result.data[graphMutations.createTranslationDeepLinkSet.operationName].pkg;
                    resolve(`${this.translationToolAdminUri}/pkg/${deepLinkPkg}/set/${deepLinkId}`);
                }
            });
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationEditingService, deps: [{ token: i3.HttpClient }, { token: GRAPH_URL_TRANSLATION }, { token: TRANS_TOOL_ADMIN_URI }, { token: InternalTranslationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationEditingService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TranslationEditingService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i3.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [GRAPH_URL_TRANSLATION]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TRANS_TOOL_ADMIN_URI]
                }] }, { type: InternalTranslationService }]; } });

class I18nPipe {
    constructor(translationService) {
        this.translationService = translationService;
    }
    transform(tag, pkg) {
        return this.translationService.translate(pkg, tag);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nPipe, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: I18nPipe, name: "i18n", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'i18n',
                    pure: false, // sad
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

class PackageConfig {
}

class UsanaTranslationsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaTranslationsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: UsanaTranslationsModule, declarations: [I18nPipe], imports: [CommonModule], exports: [I18nPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaTranslationsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaTranslationsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [I18nPipe],
                    providers: [
                    // TranslationConfig, //providedIn: root
                    // TranslationService, //providedIn: root
                    ],
                    imports: [CommonModule],
                    exports: [I18nPipe],
                }]
        }] });

class UsanaTranslationsEditingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaTranslationsEditingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: UsanaTranslationsEditingModule, imports: [CommonModule, UsanaTranslationsModule], exports: [UsanaTranslationsModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaTranslationsEditingModule, providers: [TranslationEditingService], imports: [CommonModule, UsanaTranslationsModule, UsanaTranslationsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaTranslationsEditingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    providers: [TranslationEditingService],
                    imports: [CommonModule, UsanaTranslationsModule],
                    exports: [UsanaTranslationsModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { GRAPH_URL_TRANSLATION, I18nPipe, PackageConfig, PackagePrimeMode, REST_URL_TRANSLATION, TRANS_TOOL_ADMIN_URI, TranslationConfig, TranslationEditingService, TranslationService, UsanaTranslationsEditingModule, UsanaTranslationsModule };
